"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { Control, Controller, FieldError } from "react-hook-form";
import { ProgressBarLink } from "@/components/progress-bar";
import { Label } from "@/components/ui/label";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue } from "@/components/ui/select";
import { formatToHumanReadable, formatUnit } from "@/lib/format-data";
import { canonicalMedicationsByName } from "@/lib/medication-select";
import { cx } from "@/lib/tailwind-utils";
import { Unit } from "@/types/enums/unit";
import { medicationsQueryOptions } from "@/query/medications/medications";
interface MedicationNameSelectProps {
  name: string;
  control: Control<any>;
  error?: FieldError;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  onValueChange?: (value: string) => void;
}
export function MedicationNameSelect(t0) {
  const $ = _c(28);
  const {
    name,
    control,
    error,
    label: t1,
    placeholder: t2,
    required: t3,
    className: t4,
    onValueChange
  } = t0;
  const label = t1 === undefined ? "Medication Name" : t1;
  const placeholder = t2 === undefined ? "Select medication" : t2;
  const required = t3 === undefined ? false : t3;
  const className = t4 === undefined ? "" : t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = medicationsQueryOptions.list();
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const {
    data: t6,
    isLoading
  } = useQuery(t5);
  let medicationNames;
  let medicationUnitMap;
  if ($[1] !== t6) {
    const medications = t6 === undefined ? [] : t6;
    const canonicalByName = canonicalMedicationsByName(medications);
    medicationNames = Array.from(canonicalByName.keys());
    medicationUnitMap = new Map();
    canonicalByName.forEach((med, medicationName) => {
      medicationUnitMap.set(medicationName, med.unit as Unit | null ?? null);
    });
    $[1] = t6;
    $[2] = medicationNames;
    $[3] = medicationUnitMap;
  } else {
    medicationNames = $[2];
    medicationUnitMap = $[3];
  }
  let t7;
  if ($[4] !== className) {
    t7 = cx("space-y-1", className);
    $[4] = className;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== label || $[7] !== name || $[8] !== required) {
    t8 = label && <Label htmlFor={name} mandatory={required}>{label}</Label>;
    $[6] = label;
    $[7] = name;
    $[8] = required;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== error || $[11] !== isLoading || $[12] !== medicationNames || $[13] !== medicationUnitMap || $[14] !== onValueChange || $[15] !== placeholder) {
    t9 = t10 => {
      const {
        field
      } = t10;
      return <SearchSelect value={field.value || ""} onValueChange={value => {
        field.onChange(value);
        onValueChange?.(value);
      }}><SearchSelectTrigger hasError={!!error} wrap={true}><SearchSelectValue placeholder={placeholder} formatValue={formatToHumanReadable} className="line-clamp-2" /></SearchSelectTrigger><SearchSelectContent searchPlaceholder="Search medications..." emptyMessage={isLoading ? "Loading..." : "No medications found."} className="z-[900] max-w-xs">{medicationNames.map(medication => {
            const unit = medicationUnitMap.get(medication);
            const displayName = unit ? `${formatToHumanReadable(medication)} (${formatUnit(unit)})` : formatToHumanReadable(medication);
            return <SearchSelectItem key={medication} value={medication}>{displayName}</SearchSelectItem>;
          })}<div className="border-t border-border px-3 py-2 text-body text-foreground-secondary">To add a new medication, go to{" "}<ProgressBarLink href="/settings/medications" className="text-link hover:text-link-hover underline">Settings → Medications</ProgressBarLink></div></SearchSelectContent></SearchSelect>;
    };
    $[10] = error;
    $[11] = isLoading;
    $[12] = medicationNames;
    $[13] = medicationUnitMap;
    $[14] = onValueChange;
    $[15] = placeholder;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== control || $[18] !== name || $[19] !== t9) {
    t10 = <Controller name={name} control={control} render={t9} />;
    $[17] = control;
    $[18] = name;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== error) {
    t11 = error && <p className="text-caption text-status-error-text">{error.message}</p>;
    $[21] = error;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t7 || $[26] !== t8) {
    t12 = <div className={t7}>{t8}{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t7;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}